import {
  DerivativesOhlcSignalDto,
  InstrumentOhlcDto,
  InstrumentWithStatsDto,
  StockTradingSymbolDtoCollectionContainerDto,
  StockTradingSymbolsGroupedDto,
  TradingPairContractType,
} from 'common/api/autogenerated/trading-gateway';
import { DerivativesChartsDto } from 'common/api/autogenerated/trading-gateway/models/DerivativesChartsDto';

import { AssetDtoEnhanced, AssetsHashMap, GroupsHashMap } from 'common/types';

import { ChartDataCandle } from 'components/ChartWidget/types';
import { OrderBookDto, OrderBookSnapshotDto } from 'components/OrderBook/types';

export const mapStocksGroupsFromServer = (data: StockTradingSymbolsGroupedDto[]): GroupsHashMap =>
  data.reduce<GroupsHashMap>((result, item) => {
    if (item.type) {
      return {
        ...result,
        [item.type]: {
          type: item.type,
          symbols: item.ids || [],
        },
      };
    }
    return result;
  }, {});

export const mapStocksFromServer = (stateStocks: AssetsHashMap, data: InstrumentWithStatsDto[]): AssetsHashMap =>
  data.reduce<AssetsHashMap>((result, item) => {
    if (item.symbol) {
      const {
        symbol,
        lastTradePrice,
        bestAskPrice,
        bestBidPrice,
        // status,
        id,
        tradeVolumeUsd,
        quoteCurrency,
        // settlementCurrency,
        maxLeverage,
        decimalPlaces,
        openPrice,
        highPrice,
        lowPrice,
        description,
        // contractType,
        // contractValue,
      } = item;

      const asset: AssetDtoEnhanced = {
        ...item,
        symbol,
        code: id,
        name: symbol,
        displayName: symbol,
        currency: quoteCurrency || '',
        lastTradePrice,
        bestAskPrice,
        bestBidPrice,
        trend: [],
        bid: bestBidPrice,
        ask: bestAskPrice,
        price: lastTradePrice || 0,
        prevPrice: lastTradePrice || 0,
        prevAsk: bestAskPrice || 0,
        prevBid: bestBidPrice || 0,
        // isInternal: status === TradingPairStatus.DISABLED || status === TradingPairStatus.HIDDEN,
        dailyTradeVolumeUsd: tradeVolumeUsd,
        maxLeverage,
        digits: decimalPlaces,
        openPrice,
        // contractType,
        // contractValue,
        dayChange: {
          first: openPrice,
          high: highPrice,
          low: lowPrice,
        },
        description,
        mode: 'perpetual',
      };

      if (id) {
        return {
          ...result,
          [id]: {
            ...stateStocks[id],
            ...asset,
          },
        };
      }

      return {
        ...result,
        [symbol]: {
          ...stateStocks[symbol],
          ...asset,
        },
      };
    }
    return result;
  }, {});

export const mapFavoritesFromServer = (data: StockTradingSymbolDtoCollectionContainerDto['data']) => {
  if (!data) return [];
  return data.map(({ id }) => decodeURIComponent(String(id)));
};

export const mapDerivativeCandlesFromServer = (data: DerivativesChartsDto['data'] = []): ChartDataCandle[] =>
  (data || []).reverse().map((candle) => {
    const { open = 0, high = 0, low = 0, close = 0, time = 0 } = candle;

    return { open, high, low, close, time: new Date(time || 0).getTime() };
  });
export const applyTrendToStocks = (trends: DerivativesOhlcSignalDto[], derivativesHashMap: AssetsHashMap) => {
  const trendsHashMap = trends.reduce<Record<string, DerivativesOhlcSignalDto[]>>((acc, val) => {
    if (!val.tradingPairCode) return acc;
    acc[val.tradingPairCode] = acc[val.tradingPairCode] ? acc[val.tradingPairCode].concat(val) : [val];
    return acc;
  }, {});

  Object.keys(trendsHashMap).forEach((code) => {
    if (derivativesHashMap[code])
      derivativesHashMap[code].trend = trendsHashMap[code].map((i) => i.close || 0).reverse();
  });

  return derivativesHashMap;
};

export const mapDerivativeOrderBookSnapshot = (
  snapshot: OrderBookSnapshotDto,
  asset: AssetDtoEnhanced,
): OrderBookDto => {
  const { version, asks = [], bids = [] } = snapshot;
  const { contractType, contractValue, price: lastPrice } = { ...asset };

  const buyMap = new Map<number, number>();
  const sellMap = new Map<number, number>();

  const formatAmount = (initialAmount: number) => {
    if (!contractType || !contractValue) return initialAmount;

    if (contractType === TradingPairContractType.LINEAR) {
      return initialAmount * contractValue;
    }
    if (contractType === TradingPairContractType.INVERSE) return (initialAmount * contractValue) / lastPrice;

    return initialAmount;
  };

  if (bids) {
    bids.forEach((row) => {
      const [price, amount] = row;

      buyMap.set(price, formatAmount(amount));
    });
  }

  if (asks) {
    asks.forEach((row) => {
      const [price, amount] = row;

      sellMap.set(price, formatAmount(amount));
    });
  }

  const prevVersion = version - 1;

  return {
    prevVersion,
    version,
    buy: new Map<number, number>(Array.from(buyMap).sort((left, right) => right[0] - left[0])),
    sell: new Map<number, number>(Array.from(sellMap).sort((left, right) => left[0] - right[0])),
  };
};

export const mapDerivativeOrderBookUpdate = (
  state: OrderBookDto,
  update: OrderBookSnapshotDto,
  asset: AssetDtoEnhanced,
): OrderBookDto => {
  const { buy, sell, version: prevVersion } = { ...state };
  const { version, asks = [], bids = [] } = { ...update };
  const { contractType, contractValue, price: lastPrice } = asset;

  const formatAmount = (initialAmount: number) => {
    if (!contractType || !contractValue) return initialAmount;

    if (contractType === TradingPairContractType.LINEAR) return initialAmount * contractValue;
    if (contractType === TradingPairContractType.INVERSE) return (initialAmount * contractValue) / lastPrice;

    return initialAmount;
  };

  const buyMap = new Map<number, number>(buy);
  const sellMap = new Map<number, number>(sell);

  bids.forEach((row) => {
    const [price, amount] = row;

    if (!amount) buyMap.delete(price);
    else buyMap.set(price, formatAmount(amount));
  });

  asks.forEach((row) => {
    const [price, amount] = row;

    if (!amount) sellMap.delete(price);
    else sellMap.set(price, formatAmount(amount));
  });

  return {
    prevVersion,
    version,
    buy: new Map<number, number>(Array.from(buyMap).sort((left, right) => right[0] - left[0])),
    sell: new Map<number, number>(Array.from(sellMap).sort((left, right) => left[0] - right[0])),
  };
};

export const mapStocksTrendChartsFromServer = (data: InstrumentOhlcDto[] | null | undefined = []) => {
  if (!data) return [];

  return data.map((candle) => candle.close ?? 0);
};
